var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import dayjs from 'dayjs';
import { clampDateRangeToProject, suggestCellUnit } from '@/utils/print-schedule-columns';
export function normalizeDateYmd(date) {
    if (!date)
        return '';
    var parsed = dayjs(date);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}
export function resolveProjectDateRange(projectStart, projectEnd) {
    return [normalizeDateYmd(projectStart), normalizeDateYmd(projectEnd)];
}
export function hasCompleteProjectDateRange(projectDateRange) {
    return !!(Array.isArray(projectDateRange) &&
        projectDateRange.length === 2 &&
        normalizeDateYmd(projectDateRange[0]) &&
        normalizeDateYmd(projectDateRange[1]));
}
export function isSameProjectDateRange(left, right) {
    if (!hasCompleteProjectDateRange(left) || !hasCompleteProjectDateRange(right)) {
        return false;
    }
    return (normalizeDateYmd(left[0]) === normalizeDateYmd(right[0]) &&
        normalizeDateYmd(left[1]) === normalizeDateYmd(right[1]));
}
export function hasProjectDateRangeDrift(printSettings, projectStart, projectEnd) {
    if (!projectStart || !projectEnd)
        return false;
    var current = resolveProjectDateRange(projectStart, projectEnd);
    if (!current[0] || !current[1])
        return false;
    if (!(printSettings === null || printSettings === void 0 ? void 0 : printSettings.id))
        return true;
    var saved = printSettings.project_date_range;
    if (!hasCompleteProjectDateRange(saved))
        return true;
    return !isSameProjectDateRange(saved, current);
}
export function shouldOverridePrintDateRange(printSettings, projectStart, projectEnd) {
    if (!(printSettings === null || printSettings === void 0 ? void 0 : printSettings.id) || !projectStart || !projectEnd)
        return false;
    if (!hasCompleteProjectDateRange(printSettings.project_date_range))
        return false;
    var current = resolveProjectDateRange(projectStart, projectEnd);
    if (!current[0] || !current[1])
        return false;
    return !isSameProjectDateRange(printSettings.project_date_range, current);
}
export function applyProjectDateRangeToPrintSettings(printSettings, projectStart, projectEnd) {
    if (!projectStart || !projectEnd)
        return __assign({}, printSettings);
    var projectDateRange = resolveProjectDateRange(projectStart, projectEnd);
    if (!projectDateRange[0] || !projectDateRange[1])
        return __assign({}, printSettings);
    var next = __assign(__assign({}, printSettings), { project_date_range: __spreadArrays(projectDateRange) });
    if (shouldOverridePrintDateRange(printSettings, projectStart, projectEnd)) {
        next.date_range = __spreadArrays(projectDateRange);
        next.cell_unit = suggestCellUnit(projectDateRange[0], projectDateRange[1]);
    }
    return next;
}
export function migrateLegacyPrintSettings(printSettings, publishedLargeProcesses) {
    if (!printSettings || printSettings.hidden_large_process_ids !== undefined) {
        return null;
    }
    var publishedIds = publishedLargeProcesses.map(function (_a) {
        var id = _a.id;
        return id;
    });
    // If no legacy whitelist is present, show all by default (hide none)
    if (!printSettings.large_process_ids || printSettings.large_process_ids.length === 0) {
        return __assign(__assign({}, printSettings), { hidden_large_process_ids: [] });
    }
    // Hide any newly published IDs not present in the legacy whitelist
    var hidden = publishedIds.filter(function (id) { return !printSettings.large_process_ids.includes(id); });
    return __assign(__assign({}, printSettings), { hidden_large_process_ids: hidden });
}
export function resolveHiddenLargeProcessIds(publishedLargeProcesses, printSettings) {
    var publishedIds = publishedLargeProcesses.map(function (_a) {
        var id = _a.id;
        return id;
    });
    if (printSettings.hidden_large_process_ids !== undefined) {
        return printSettings.hidden_large_process_ids.filter(function (id) { return publishedIds.includes(id); });
    }
    return [];
}
export function resolveVisibleLargeProcessIds(publishedLargeProcesses, printSettings) {
    var publishedIds = publishedLargeProcesses.map(function (_a) {
        var id = _a.id;
        return id;
    });
    if (!printSettings || !printSettings.id) {
        return publishedIds;
    }
    var hiddenIds = resolveHiddenLargeProcessIds(publishedLargeProcesses, printSettings);
    return publishedIds.filter(function (id) { return !hiddenIds.includes(id); });
}
export function buildLargeProcessPrintSettingsPayload(publishedLargeProcesses, selectedIds) {
    var publishedIds = publishedLargeProcesses.map(function (_a) {
        var id = _a.id;
        return id;
    });
    return {
        large_process_ids: selectedIds,
        hidden_large_process_ids: publishedIds.filter(function (id) { return !selectedIds.includes(id); })
    };
}
export function filterPublishedLargeProcessesByVisibility(publishedLargeProcesses, printSettings) {
    var visibleIds = resolveVisibleLargeProcessIds(publishedLargeProcesses, printSettings);
    return publishedLargeProcesses.filter(function (_a) {
        var id = _a.id;
        return visibleIds.includes(id);
    });
}
export function filterPublishedLargeProcessesBySelection(publishedLargeProcesses, selectedIds) {
    var selectedIdSet = new Set(selectedIds);
    return publishedLargeProcesses.filter(function (_a) {
        var id = _a.id;
        return selectedIdSet.has(id);
    });
}
export function hasSelectedLargeProcesses(selectedIds) {
    var _a;
    return ((_a = selectedIds === null || selectedIds === void 0 ? void 0 : selectedIds.length) !== null && _a !== void 0 ? _a : 0) > 0;
}
export function resolvePrintSettingsDateRange(printSettings, fallbackDateRange) {
    var _a = (printSettings === null || printSettings === void 0 ? void 0 : printSettings.date_range) || ['', ''], savedStart = _a[0], savedEnd = _a[1];
    if (savedStart && savedEnd) {
        return [savedStart, savedEnd];
    }
    return __spreadArrays(fallbackDateRange);
}
export function resolveCellUnit(printSettings, dateRange) {
    var start = dateRange[0], end = dateRange[1];
    if (!start || !end) {
        return (printSettings === null || printSettings === void 0 ? void 0 : printSettings.cell_unit) || 'daily';
    }
    var _a = (printSettings === null || printSettings === void 0 ? void 0 : printSettings.date_range) || ['', ''], savedStart = _a[0], savedEnd = _a[1];
    var matchesSavedDateRange = savedStart === start && savedEnd === end;
    // A saved cell_unit is only meaningful for the date range it was chosen for.
    // If the range actually being rendered (e.g. from /schedule/pdf/ query params)
    // differs from the saved one, re-suggest a unit instead of blindly reusing a
    // unit (typically "daily") that could explode into far more columns/pages
    // than intended for the new range.
    if ((printSettings === null || printSettings === void 0 ? void 0 : printSettings.cell_unit) && matchesSavedDateRange) {
        return printSettings.cell_unit;
    }
    return suggestCellUnit(start, end);
}
export function buildEffectivePrintSettings(printSettings, publishedSchedule, publishedLargeProcesses, projectDateBounds) {
    var clampStart = (projectDateBounds === null || projectDateBounds === void 0 ? void 0 : projectDateBounds[0]) || publishedSchedule.start_date;
    var clampEnd = (projectDateBounds === null || projectDateBounds === void 0 ? void 0 : projectDateBounds[1]) || publishedSchedule.end_date;
    var fallbackDateRange = [
        publishedSchedule.start_date,
        publishedSchedule.end_date
    ];
    if (printSettings === null || printSettings === void 0 ? void 0 : printSettings.id) {
        var dateRange_1 = clampDateRangeToProject(resolvePrintSettingsDateRange(printSettings, fallbackDateRange), clampStart, clampEnd);
        return __assign(__assign({}, printSettings), { date_range: dateRange_1, cell_unit: resolveCellUnit(printSettings, dateRange_1) });
    }
    var allProcessIds = publishedLargeProcesses.map(function (_a) {
        var id = _a.id;
        return id;
    });
    var dateRange = __spreadArrays(fallbackDateRange);
    return {
        id: '',
        date_range: dateRange,
        cell_unit: suggestCellUnit(dateRange[0], dateRange[1]),
        large_process_ids: allProcessIds,
        hidden_large_process_ids: [],
        is_small_process: false,
        is_company_logo: false,
        is_coloring: false,
        note: ''
    };
}
